import React from 'react';
import styled from 'styled-components';
import {
  brandColours,
  breakpointSizes,
  fluidFontSize,
  fontSize,
  fontWeights,
  minBreakpointQuery,
  sectionPaddings,
  standardColours,
} from '../styles';
import { Button, Container, HtmlContent, Link } from './ui';
import rightArrow from '../images/right-arrow.svg';

const StyledBanner = styled.section`
  ${sectionPaddings('30px', '80px')};
  background-color: ${brandColours.primary};
`;

const StyledInner = styled.div`
  display: grid;
  gap: 40px;

  ${minBreakpointQuery.small`
    gap: 60px;
  `}

  ${minBreakpointQuery.medium`
    grid-template-columns: 1fr 1.2fr;
    align-items: center;
  `}

  ${minBreakpointQuery.mlarge`
    grid-template-columns: 1fr 1.4fr;
  `}

  ${minBreakpointQuery.large`
    gap: 80px;
  `}

  ${minBreakpointQuery.xxlarge`
    gap: 100px;
  `}
`;

const StyledContent = styled.div``;

const StyledHeading = styled.h1`
  color: ${brandColours.secondary};
  ${fluidFontSize(
    '30px',
    '50px',
    breakpointSizes.tiny,
    breakpointSizes.xxxxlarge,
  )};
  line-height: 1.2;
`;

const StyledText = styled(HtmlContent)`
  margin-top: 18px;

  ${minBreakpointQuery.small`
    margin-top: 22px;
  `}

  ${minBreakpointQuery.large`
    margin-top: 26px;
  `}
`;

const StyledButton = styled(Button)`
  margin-top: 18px;

  ${minBreakpointQuery.small`
    margin-top: 22px;
  `}

  ${minBreakpointQuery.large`
    margin-top: 26px;
  `}
`;

const StyledImageWrapper = styled.div`
  display: grid;
`;

const StyledImage = styled.img`
  grid-area: 1/1/1/1;
  width: 100%;
  max-height: 320px;

  ${minBreakpointQuery.tiny`
    max-height: 360px;
  `}

  ${minBreakpointQuery.small`
    max-height: 400px;
  `}

  ${minBreakpointQuery.large`
    max-height: 440px;
  `}

  ${minBreakpointQuery.xxlarge`
    max-height: 480px;
  `}
`;

const StyledPartnerBadges = styled.div`
  grid-area: 1/1/1/1;
  display: flex;
  flex-direction: column;
  justify-content: end;
  align-items: end;
  gap: 8px;
  margin-bottom: 10px;

  ${minBreakpointQuery.tiny`
    margin-bottom: 20px;
  `}

  ${minBreakpointQuery.small`
    gap: 10px;
    margin-bottom: 30px;
  `}

  ${minBreakpointQuery.medium`
    margin-bottom: 40px;
  `}

  ${minBreakpointQuery.large`
    gap: 12px;
    margin-bottom: 50px;
  `}

  ${minBreakpointQuery.xxlarge`
    margin-bottom: 60px;
  `}
`;

const StyledPartnerBadge = styled.img`
  width: 60px;
  aspect-ratio: 1/1;

  ${minBreakpointQuery.tiny`
    width: 70px;
  `}

  ${minBreakpointQuery.small`
    width: 80px;
  `}

  ${minBreakpointQuery.medium`
    width: 90px;
  `}

  ${minBreakpointQuery.large`
    width: 100px;
  `}

  ${minBreakpointQuery.xxlarge`
    width: 110px;
  `}

  ${minBreakpointQuery.xxxxlarge`
    width: 120px;
  `}
`;

const StyledPlatforms = styled.ul`
  display: grid;
  gap: 15px;
  margin-top: 30px;

  ${minBreakpointQuery.tsmall`
    grid-template-columns: repeat(2, 1fr);
  `}

  ${minBreakpointQuery.small`
    gap: 20px;
    margin-top: 40px;
  `}

  ${minBreakpointQuery.medium`
    gap: 25px;
    margin-top: 50px;
  `}

  ${minBreakpointQuery.large`
    grid-template-columns: repeat(4, 1fr);
  `}

  ${minBreakpointQuery.xlarge`
    gap: 30px;
    margin-top: 60px;
  `}
`;

const StyledPlatform = styled.li``;

const StyledPlatformLink = styled(Link)`
  --padding: 16px;
  display: flex;
  align-items: center;
  gap: 16px;
  padding: var(--padding);
  height: 100%;
  color: ${brandColours.secondary};
  font-weight: ${fontWeights.semibold};
  background: url(${rightArrow}) no-repeat calc(100% - var(--padding)) 50% /
    20px 20px ${standardColours.white};
  border-radius: 20px;

  ${minBreakpointQuery.smedium`
    ${fontSize(17)};
  `}

  ${minBreakpointQuery.mlarge`
    --padding: 18px;
    gap: 18px;
  `}

  ${minBreakpointQuery.xlarge`
    ${fontSize(18)};
  `}

  ${minBreakpointQuery.xxxlarge`
    --padding: 20px;
    gap: 20px;
  `}
`;

const StyledPlatformBadge = styled.img`
  padding: 10px;
  width: 50px;
  background-color: ${brandColours.primary};
  border-radius: 10px;

  ${minBreakpointQuery.small`
    width: 60px;
  `}
`;

const StyledPlatformText = styled.span`
  width: min-content;
`;

const Banner = ({ heading, text, link, image, partnerBadges, platforms }) => (
  <StyledBanner>
    <Container>
      <StyledInner>
        <StyledContent>
          <StyledHeading
            dangerouslySetInnerHTML={{
              __html: heading.replace(/(<p>|<\/p>)/g, ''),
            }}
          />
          <StyledText content={text} />
          <StyledButton to={link.pageUrl}>{link.text}</StyledButton>
        </StyledContent>
        <StyledImageWrapper>
          <StyledImage src={image.url} alt={image.alt} loading="eager" />
          {partnerBadges.length > 0 && (
            <StyledPartnerBadges>
              {partnerBadges.map(({ url, alt }, id) => (
                <StyledPartnerBadge
                  key={id}
                  src={url}
                  alt={alt}
                  loading="eager"
                />
              ))}
            </StyledPartnerBadges>
          )}
        </StyledImageWrapper>
      </StyledInner>
      {platforms.length > 0 && (
        <StyledPlatforms>
          {platforms.map((platform, id) => {
            const { badge, title } = platform;

            return (
              <StyledPlatform key={id}>
                <StyledPlatformLink to={platform}>
                  <StyledPlatformBadge
                    src={badge.url}
                    alt={badge.alt}
                    loading="eager"
                  />
                  <StyledPlatformText>{title}</StyledPlatformText>
                </StyledPlatformLink>
              </StyledPlatform>
            );
          })}
        </StyledPlatforms>
      )}
    </Container>
  </StyledBanner>
);

export default Banner;
